import { useMemo, useState } from 'react';
import type { Pose, PoseGroup, Posture } from '@studio/shared';

import { Carousel } from './Carousel';
import { FilterChips } from './FilterChips';
import { OptionCard } from './OptionCard';
import { useExamples } from '../lib/useExamples';
import { usePresetLabel, useT } from '../lib/i18n';

interface Props {
  poses: Pose[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Frames per set-up, so the picker can say how the poses will be spread. */
  variants: number;
}

/**
 * Poses are a second axis alongside camera angles: pick several and the frames
 * of each set-up cycle through them, so a set-up returns different positions
 * rather than the same one re-rolled.
 */
export function PosePicker({ poses, selected, onChange, variants }: Props) {
  const tp = usePresetLabel();
  const t = useT();
  const examples = useExamples();
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState<PoseGroup | 'all'>('all');

  const groups = useMemo(
    () =>
      [...new Set(poses.map((p) => p.group))].map((id) => ({
        id,
        label: t(`group.${id}`),
        count: poses.filter((p) => p.group === id).length,
      })),
    [poses, t],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return poses.filter(
      (p) =>
        (group === 'all' || p.group === group) &&
        (!q ||
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.posture.includes(q)),
    );
  }, [poses, query, group]);

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  // More positions than frames means the extras never get shot.
  const unused = selected.length > variants ? selected.length - variants : 0;

  return (
    <div className="stack stack--sm">
      <div className="filter-row">
        <input
          className="input"
          type="search"
          value={query}
          placeholder={t('pose.search')}
          aria-label={t('pose.searchAria')}
          onChange={(event) => setQuery(event.target.value)}
        />
        {selected.length > 0 && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => onChange([])}>
            Clear {selected.length}
          </button>
        )}
      </div>

      <FilterChips groups={groups} active={group} onChange={setGroup} total={poses.length} />

      {unused > 0 && (
        <div className="banner">
          <span>
            {selected.length} positions but only {variants} frame{variants === 1 ? '' : 's'} per set-up —{' '}
            {unused} would never be shot. Raise frames per set-up to see them all.
          </span>
        </div>
      )}

      {visible.length === 0 ? (
        <p className="faint">{t('picker.noMatch', { query })}</p>
      ) : (
        <Carousel
          count={
            selected.length
              ? t('picker.chosen', { n: selected.length })
              : t('picker.available', { n: visible.length })
          }
        >
          {visible.map((pose) => (
            <OptionCard
              key={pose.id}
              title={tp(`pose.${pose.id}.name`, pose.name)}
              description={tp(`pose.${pose.id}.desc`, pose.description)}
              spec={`${t(`posture.${pose.posture}`)}${pose.needsSupport ? ` · ${t('pose.needsSupport')}` : ''}`}
              selected={selected.includes(pose.id)}
              order={selected.includes(pose.id) ? selected.indexOf(pose.id) + 1 : undefined}
              example={examples[`pose-${pose.id}`]}
              fallback={<PostureFigure posture={pose.posture} />}
              onToggle={() => toggle(pose.id)}
            />
          ))}
        </Carousel>
      )}
    </div>
  );
}

/** A stick figure in the actual posture, for cards with no photograph yet. */
function PostureFigure({ posture }: { posture: Posture }) {
  return (
    <svg viewBox="0 0 46 58" aria-hidden="true">
      <g stroke="var(--ink-3)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round">
        {FIGURES[posture]}
      </g>
    </svg>
  );
}

const FIGURES: Record<Posture, JSX.Element> = {
  standing: (
    <>
      <circle cx="23" cy="13" r="4.5" />
      <path d="M23 17.5v18M15 24l8-4 8 4M23 35.5l-5 15M23 35.5l5 15" />
    </>
  ),
  walking: (
    <>
      <circle cx="23" cy="13" r="4.5" />
      <path d="M23 17.5v18M14 27l9-7 8 5M23 35.5l-8 14M23 35.5l7 15" />
    </>
  ),
  seated: (
    <>
      <circle cx="20" cy="18" r="4.5" />
      <path d="M20 22.5v13M13 29l7-5 7 4M20 35.5h13M33 35.5v13" />
    </>
  ),
  reclining: (
    <>
      <circle cx="12" cy="34" r="4.5" />
      <path d="M16.5 34h17M16 30l6-4 5 5M33.5 34l8 8M12 38.5l4 5" />
    </>
  ),
  leaning: (
    <>
      <circle cx="20" cy="14" r="4.5" />
      <path d="M20 18.5l3 17M15 25l5-4 12 2M23 35.5l-4 14M23 35.5l6 14M36 12v34" />
    </>
  ),
  crouching: (
    <>
      <circle cx="23" cy="20" r="4.5" />
      <path d="M23 24.5v10M16 30l7-5 7 5M23 34.5l-7 6 3 8M23 34.5l7 6-3 8" />
    </>
  ),
};
